import React, { useState, useEffect } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, FileText, MinusCircle, Home, TrendingUp } from 'lucide-react';

const ScraperMonitoring = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [search, setSearch] = useState('');
  const [price, setPrice] = useState(null);

  const fetchHealth = async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/scrapers/health?_=${Date.now()}`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(`Failed to load scraper health${err?.message ? ` (${err.message})` : ''}`);
    } finally {
      setLoading(false);
    }
  };

  // The price pipeline is fetched separately and failure here is swallowed: it
  // is a panel on someone else's page, and it must never take the scraper view
  // down with it.
  const fetchPrice = async () => {
    try {
      const res = await fetch(`/api/scrapers/price-pipeline?_=${Date.now()}`,
                              { headers: { Accept: 'application/json' } });
      setPrice(res.ok ? await res.json() : null);
    } catch { setPrice(null); }
  };

  useEffect(() => {
    fetchHealth();
    fetchPrice();
    const interval = setInterval(() => { fetchHealth(); fetchPrice(); }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const statusIcon = (status, hoursAgo) => {
    if (status === 'inactive') return <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full"><MinusCircle size={10} />Inactive</span>;
    if (status === 'local') return <span className="inline-flex items-center gap-1 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full"><Home size={10} />Local</span>;
    if (status === 'running') return <span className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full"><Clock size={10} />Running</span>;
    if (status === 'failed') return <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full"><XCircle size={10} />Failed</span>;
    if (status === 'success' && hoursAgo < 26) return <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full"><CheckCircle size={10} />Healthy</span>;
    return <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-0.5 rounded-full"><AlertTriangle size={10} />Stale</span>;
  };

  const fmtTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    const h = Math.round((Date.now() - d) / 3600000);
    if (h < 1) return 'Just now';
    if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
  };

  const summary = data?.summary_24h || [];
  const successes = summary.find(s => s.status === 'success')?.count || 0;
  const failures = summary.find(s => s.status === 'failed')?.count || 0;
  const scrapers = (data?.scrapers || []).filter(s =>
    !search || s.scraper_name.toLowerCase().includes(search.toLowerCase())
  );
  const cs = data?.content_scraper || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-light text-gray-900">Scraper Monitoring</h1>
          <p className="text-sm text-gray-500 mt-1">
            Live status of all short seller scrapers
            {lastUpdated && <span className="ml-2 text-gray-400">· Updated {fmtTime(lastUpdated)}</span>}
          </p>
        </div>
        <button
          onClick={() => { fetchHealth(); fetchPrice(); }}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Total Scrapers</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data?.scrapers?.length || '—'}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Success (24h)</p>
          <p className="text-2xl font-bold text-green-600 mt-1">{successes}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Failed (24h)</p>
          <p className="text-2xl font-bold text-red-600 mt-1">{failures}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs text-gray-500 uppercase tracking-wide">Pending Content</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{data?.pending_content ?? '—'}</p>
        </div>
      </div>

      {/* Price pipeline — staleness first.
          This panel exists because the pipeline fails SILENTLY: when the nightly
          task stops, nothing errors, the dashboard just keeps serving the prices
          it already had. That happened for nine days in Sept/Oct 2026. So the
          headline is how far behind the data is, not whether a run "succeeded". */}
      {price && (
        <div className={`bg-white border rounded-lg overflow-hidden ${
          price.status === 'stale' ? 'border-red-300'
          : price.status === 'lagging' ? 'border-yellow-300'
          : 'border-gray-200'}`}>
          <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <TrendingUp size={14} className="text-gray-400" />
            <h3 className="text-sm font-semibold text-gray-700">Price Pipeline</h3>
            <span className="text-xs text-gray-400">— Fargate task · nightly 23:00 UTC, weekdays</span>
            <span className="ml-auto">
              {price.status === 'ok' && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full">
                  <CheckCircle size={10} />Current
                </span>
              )}
              {price.status === 'lagging' && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-yellow-700 bg-yellow-50 border border-yellow-200 px-2 py-0.5 rounded-full">
                  <AlertTriangle size={10} />Lagging
                </span>
              )}
              {price.status === 'stale' && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full">
                  <XCircle size={10} />Stale — refresh not running
                </span>
              )}
            </span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100">
            <div className="px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Days Behind</p>
              <p className={`text-2xl font-bold ${
                price.status === 'stale' ? 'text-red-600'
                : price.status === 'lagging' ? 'text-yellow-600' : 'text-green-600'}`}>
                {price.staleness.trading_days_behind}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">trading days</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Latest Bar</p>
              <p className="text-sm font-medium text-gray-700 mt-1">{price.staleness.latest_bar || '—'}</p>
              <p className="text-xs text-gray-400 mt-0.5">last swept {fmtTime(price.instruments.last_sweep)}</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Instruments Current</p>
              <p className="text-sm font-medium text-gray-700 mt-1">
                {price.instruments.current} <span className="text-gray-400">/ {price.instruments.total}</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">holding the latest bar</p>
            </div>
            <div className="px-4 py-3">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Reports Unpriced</p>
              <p className={`text-sm font-medium mt-1 ${
                price.reports_30d.unmapped > 0 ? 'text-yellow-700' : 'text-gray-700'}`}>
                {price.reports_30d.unmapped} <span className="text-gray-400">/ {price.reports_30d.total}</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">last 30d · shows as “—” in the table</p>
            </div>
          </div>

          {price.recent_runs?.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Recent Sweeps</p>
              <div className="space-y-1">
                {price.recent_runs.slice(0, 5).map((r) => (
                  <div key={r.day} className="flex items-center gap-3 text-xs">
                    <span className="font-mono text-gray-600 w-24">{r.day}</span>
                    <span className="text-green-700">{r.ok} ok</span>
                    {r.failed > 0 && <span className="text-red-600">{r.failed} failed</span>}
                    <span className="text-gray-400 ml-auto">{r.bars.toLocaleString()} bars</span>
                  </div>
                ))}
              </div>
              {/* A gap between these dates IS the alert — a missed night leaves no
                  row at all, so absence is the signal rather than a failure count. */}
              <p className="text-xs text-gray-400 mt-2">
                One row per day the sweep ran. A missing weekday means it did not run.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Content Scraper Panel */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
          <FileText size={14} className="text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-700">Content Scraper</h3>
          <span className="text-xs text-gray-400 ml-1">— Fargate task · processes report PDFs &amp; HTML</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100">
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Pending</p>
            <p className={`text-xl font-bold ${cs.pending > 0 ? 'text-amber-500' : 'text-gray-900'}`}>
              {cs.pending ?? '—'}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Processed (24h)</p>
            <p className="text-xl font-bold text-green-600">{cs.completed_24h ?? '—'}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Failed (24h)</p>
            <p className={`text-xl font-bold ${cs.failed_24h > 0 ? 'text-red-600' : 'text-gray-400'}`}>
              {cs.failed_24h ?? '—'}
            </p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Last Processed</p>
            <p className="text-sm font-medium text-gray-700">{fmtTime(cs.last_processed_at)}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 divide-x divide-gray-100 border-t border-gray-100">
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Completed</p>
            <p className="text-sm font-medium text-gray-700">{cs.total_completed?.toLocaleString() ?? '—'}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Failed</p>
            <p className="text-sm font-medium text-gray-700">{cs.total_failed?.toLocaleString() ?? '—'}</p>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg Attempts</p>
            <p className="text-sm font-medium text-gray-700">{cs.avg_attempts ?? '—'}</p>
          </div>
        </div>
        {cs.recent_errors?.length > 0 && (
          <div className="border-t border-gray-100">
            <div className="px-4 py-2 text-xs font-medium text-gray-500 uppercase tracking-wide bg-red-50">
              Recent Failures (24h)
            </div>
            <div className="divide-y divide-gray-100">
              {cs.recent_errors.map((e, i) => (
                <div key={i} className="px-4 py-2 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-800 truncate">{e.report_title || 'Untitled'}</p>
                    <p className="text-xs text-gray-400">{e.short_seller} · attempt {e.scrape_attempts}</p>
                    {e.last_scrape_error && (
                      <p className="text-xs text-red-500 truncate mt-0.5">{e.last_scrape_error}</p>
                    )}
                  </div>
                  <span className="text-xs text-gray-400 whitespace-nowrap">{fmtTime(e.last_scrape_attempt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Search */}
      <input
        type="text"
        placeholder="Filter scrapers…"
        value={search}
        onChange={e => setSearch(e.target.value)}
        className="w-full max-w-xs px-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {/* Table */}
      {error ? (
        <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg">{error}</div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Scraper</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Found</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">New</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Run</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Error</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && !data ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : scrapers.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No scrapers found</td></tr>
              ) : scrapers.map(s => (
                <tr key={s.scraper_name} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{s.scraper_name}</td>
                  <td className="px-4 py-3">{statusIcon(s.status, s.hours_ago)}</td>
                  <td className="px-4 py-3 text-right text-gray-700">{s.reports_found ?? '—'}</td>
                  <td className={`px-4 py-3 text-right font-medium ${s.reports_new > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                    {s.reports_new > 0 ? `+${s.reports_new}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{fmtTime(s.started_at)}</td>
                  <td className="px-4 py-3 text-xs text-red-500 max-w-xs truncate">{s.error_message || ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-400">
            {scrapers.length} scrapers · auto-refreshes every 5 min
          </div>
        </div>
      )}
    </div>
  );
};

export default ScraperMonitoring;
